"use client";

import Image from "next/image";
import Link from "next/link";
import { ArrowRight, CheckCircle2, Sparkles, Star } from "lucide-react";

import { TrackedCTALink } from "@/components/analytics/tracked-cta";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/components/i18n-provider";
import { useCvsReviewedCount } from "@/hooks/use-cvs-reviewed-count";
import { formatCvsReviewed, formatRating } from "@/lib/proof-stats";

const HERO_IMAGE = "/assets/penawaran-hero.png";

export function OfferHeroBand({
  publicMode = false,
}: {
  publicMode?: boolean;
}) {
  const { locale, dictionary } = useI18n();
  const cvsReviewed = useCvsReviewedCount();
  const copy = dictionary.dashboard.offerPage.hero;
  // Proof numbers come from the proof-stats module (verifiable, rounded down);
  // the dictionary only carries the label.
  const topStat = {
    value: formatCvsReviewed(locale, cvsReviewed),
    label: dictionary.home.stats.cvsReviewed,
  };

  // Career OS is auth-gated; public visitors route through sign-up first.
  const careerOsPath = `/${locale}/dashboard/career-os`;
  const learnMoreHref = publicMode
    ? `/${locale}/sign-up?redirect=${encodeURIComponent(careerOsPath)}`
    : careerOsPath;

  if (!publicMode) {
    return (
      <section className="grid gap-8 lg:grid-cols-[1.4fr_1fr] lg:items-start">
        <div className="max-w-xl space-y-4">
          <p className="flex items-center gap-1.5 text-sm font-semibold text-brand-primary">
            <Sparkles className="size-4" aria-hidden="true" />
            {copy.eyebrow}
          </p>
          <h2 className="text-3xl font-bold leading-tight tracking-tight text-text-heading md:text-4xl md:leading-[1.1]">
            {copy.heading}
          </h2>
          <p className="text-base leading-7 text-text-muted">{copy.supporting}</p>
        </div>

        <div className="rounded-2xl border border-border-primary bg-card p-5 shadow-card">
          <p className="flex items-center gap-2 text-sm font-semibold text-text-heading">
            <Sparkles className="size-4 text-brand-primary" aria-hidden="true" />
            {copy.firstTimeTitle}
          </p>
          <ul className="mt-4 space-y-2.5">
            {copy.firstTimePoints.map((point) => (
              <li
                key={point}
                className="flex items-start gap-2 text-sm text-text-body"
              >
                <CheckCircle2
                  className="mt-0.5 size-4 flex-shrink-0 text-green-600"
                  aria-hidden="true"
                />
                {point}
              </li>
            ))}
          </ul>
          <Button
            asChild
            variant="outline"
            className="mt-5 min-h-11 w-full justify-between border-border-primary text-text-heading"
          >
            <Link href={learnMoreHref}>
              {copy.learnMore}
              <ArrowRight className="size-4" aria-hidden="true" />
            </Link>
          </Button>
        </div>
      </section>
    );
  }

  return (
    <section className="grid items-center gap-10 lg:grid-cols-[1.05fr_0.95fr] lg:gap-14">
      <div className="max-w-xl">
        <p className="inline-flex items-center gap-2 text-sm font-semibold text-brand-primary">
          <Sparkles className="size-4" aria-hidden="true" />
          {copy.eyebrow}
        </p>
        <h1 className="mt-4 text-3xl font-semibold leading-[1.1] tracking-[-0.5px] text-text-heading sm:text-4xl lg:text-5xl lg:leading-[1.08] lg:tracking-[-1.2px]">
          {copy.heading}
        </h1>
        <p className="mt-5 max-w-md text-lg leading-7 text-text-muted">
          {copy.supporting}
        </p>
        <div className="mt-7 flex flex-wrap gap-3">
          <Button
            asChild
            className="min-h-12 bg-brand-primary px-6 text-base font-semibold text-white hover:bg-brand-primary-hover"
          >
            <TrackedCTALink
              href={`/${locale}/review-cv-gratis`}
              context="offer_hero_primary"
              locale={locale}
            >
              {copy.ctaPrimary}
              <ArrowRight className="size-4" aria-hidden="true" />
            </TrackedCTALink>
          </Button>
          <Button
            asChild
            variant="outline"
            className="min-h-12 border-border-primary px-6 text-base font-semibold text-text-heading"
          >
            <Link href="#career-os-bundle">{copy.ctaSecondary}</Link>
          </Button>
        </div>
        <div className="mt-8 flex flex-wrap items-center gap-3">
          <span className="flex gap-0.5 text-amber-400" aria-hidden="true">
            {Array.from({ length: 5 }).map((_, index) => (
              <Star key={index} className="size-[18px] fill-current" />
            ))}
          </span>
          <span className="text-sm text-text-muted">
            <b className="font-semibold text-text-heading">
              {formatRating(locale)}
            </b>{" "}
            {copy.ratingNote}
          </span>
        </div>
      </div>

      <div className="relative">
        <Image
          src={HERO_IMAGE}
          alt={copy.heading}
          width={1690}
          height={931}
          priority
          sizes="(max-width: 1024px) 100vw, 50vw"
          className="aspect-[16/11] w-full rounded-3xl object-cover shadow-card"
        />
        <div className="absolute left-4 bottom-4 flex items-center gap-3 rounded-2xl border border-border-primary bg-card px-4 py-3 shadow-card sm:left-6 sm:bottom-6">
          <span className="flex size-11 flex-none items-center justify-center rounded-xl bg-green-100 text-green-700">
            <CheckCircle2 className="size-6" aria-hidden="true" />
          </span>
          <span>
            <span className="block text-xl font-bold leading-none text-text-heading">
              {topStat.value}
            </span>
            <span className="mt-1 block text-xs text-text-muted">
              {topStat.label}
            </span>
          </span>
        </div>
      </div>
    </section>
  );
}
